import React, { useState, useEffect, useMemo } from 'react';
import { FiDelete, FiPlus, FiMinus, FiTag } from 'react-icons/fi'; // Added FiTag
import { Transaction, TagMap } from '../types'; // Keep Transaction type
import { formatCurrency } from '../utils/formatters';
import DraggableBottomSheet from './DraggableBottomSheet';
import TagSelector from './TagSelector';
import CurrencyDisplay from './AmountDisplay';
import Badge from './Badge';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchTagsByType } from '../store/slices/tagsSlice';
import { useDisplayPrivacy } from '../contexts/DisplayPrivacyContext';
import {
    buildTransactionDateTime,
    formatTransactionDateInputValue,
    getTodayKolkataDateInputValue,
} from '../utils/dateTimeUtils';


interface AmountInputModalProps {
    onClose: () => void;
    transaction: Transaction;
    onSubmitTransaction: (updatedTransaction: Transaction) => Promise<void>;
    tagMap: TagMap; // Add tagMap prop
    mode?: 'create' | 'split'; // Change from 'normal' to 'create'
    maxAmount?: number; // Add optional maxAmount for split mode
}

const AmountInputModal: React.FC<AmountInputModalProps> = ({
    onClose,
    transaction,
    onSubmitTransaction,
    tagMap, // Add tagMap parameter
    mode = 'create', // Change default from 'normal' to 'create'
    maxAmount, // Add maxAmount parameter
}) => {
    // --- State ---
    // Initialize state based on the passed transaction object
    const [amountString, setAmountString] = useState(mode === 'split' ? '0' : transaction.amount > 0 ? String(transaction.amount) : '0');
    const [description, setDescription] = useState(transaction.description || '');
    const initialDateInputValue = useMemo(() => {
        return transaction.transactionDate
            ? formatTransactionDateInputValue(transaction.transactionDate)
            : getTodayKolkataDateInputValue();
    }, [transaction.transactionDate]);
    const [transactionDateInputValue, setTransactionDateInputValue] = useState<string>(initialDateInputValue);
    const [selectedTagName, setSelectedTagName] = useState<string | undefined>(transaction.tag ?? undefined);
    const [transactionType, setTransactionType] = useState<'CREDIT' | 'DEBIT'>(transaction.type || 'DEBIT');

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAnimatingOut, setIsAnimatingOut] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [isTagSelectorOpen, setIsTagSelectorOpen] = useState(false); // New state for tag selector
    const dispatch = useAppDispatch();
    const { randomizeAmounts } = useDisplayPrivacy();
    const { debitTags, creditTags, debitStatus, creditStatus } = useAppSelector((state) => state.tags);

    // Get tag name for display using the tagMap
    const tagName = useMemo(() => selectedTagName || 'TAG', [selectedTagName]);
    const availableTags = useMemo(
        () => (transactionType === 'CREDIT' ? creditTags : debitTags),
        [transactionType, creditTags, debitTags]
    );

    // --- Effects ---
    useEffect(() => {
        const timer = setTimeout(() => setIsVisible(true), 10);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (transactionType === 'CREDIT' && creditStatus === 'idle') {
            dispatch(fetchTagsByType('CREDIT'));
            return;
        }

        if (transactionType === 'DEBIT' && debitStatus === 'idle') {
            dispatch(fetchTagsByType('DEBIT'));
        }
    }, [dispatch, transactionType, creditStatus, debitStatus]);

    useEffect(() => {
        if (!selectedTagName) {
            return;
        }

        const selectedTagIsAvailable = availableTags.some((parent) =>
            parent.name === selectedTagName || (parent.children ?? []).some((child) => child.name === selectedTagName)
        );

        if (!selectedTagIsAvailable) {
            setSelectedTagName(undefined);
        }
    }, [availableTags, selectedTagName]);

    // --- Handlers ---
    const handleClose = () => {
        setIsAnimatingOut(true);
        setTimeout(() => onClose(), 300);
    };

    const handleNumpadClick = (value: string) => {
        setError(null);
        if (amountString.includes('.') && value === '.') return;
        let nextAmountString = (amountString === '0' && value !== '.') ? value : amountString + value;
        const parts = nextAmountString.split('.');
        if (parts.length > 1 && parts[1].length > 2) return;
        const potentialAmount = parseFloat(nextAmountString);
        if (!isNaN(potentialAmount)) {
            if (potentialAmount > 999999999) return; // General large amount check
            // Add check for maxAmount in split mode
            if (mode === 'split' && maxAmount !== undefined && potentialAmount > maxAmount) {
                // Optionally set an error or just prevent update
                // setError(`Amount cannot exceed ${formatCurrency(maxAmount)}`);
                return; // Prevent exceeding max amount
            }
        }
        // transaction.amount = parseFloat(nextAmountString); // Removed: Mutates prop/state directly
        setAmountString(nextAmountString);
    };

    const handleBackspace = () => {
        setError(null);
        setAmountString(prev => prev.length > 1 ? prev.slice(0, -1) : '0');
    };

    const setTransactionTypeAndSync = (type: 'CREDIT' | 'DEBIT') => {
        setTransactionType(type);
    };

    // New handlers for tag selection
    const openTagSelector = () => {
        setIsTagSelectorOpen(true);
    };

    const closeTagSelector = () => {
        setIsTagSelectorOpen(false);
    };

    const handleSelectTag = (tagNameValue: string | null) => {
        setSelectedTagName(tagNameValue === null ? undefined : tagNameValue);
        closeTagSelector();
    };

    const handleSubmit = async () => {
        setError(null);
        const finalAmount = parseFloat(amountString);

        if (finalAmount <= 0) {
            setError("Amount must be greater than zero.");
            return;
        }

        // Add validation for maxAmount in split mode
        if (mode === 'split' && maxAmount !== undefined && finalAmount >= maxAmount) {
             setError(`Split amount must be less than the remaining amount (${formatCurrency(maxAmount, { randomize: randomizeAmounts })}).`);
            return;
        }

        setIsSubmitting(true);
        try {
            // Create the updated transaction object based on current state
            const updatedTransaction: Transaction = {
                ...transaction, // Spread the original transaction to keep id, account, etc.
                amount: finalAmount,
                description: description,
                transactionDate: buildTransactionDateTime(transactionDateInputValue, transaction.transactionDate),
                type: transactionType,
                tag: selectedTagName ?? null,
                // Ensure required fields are present if they weren't on the dummy
                uniqueKey: transaction.uniqueKey || `new_${Date.now()}`, // Example placeholder
                excludeFromAccounting: transaction.excludeFromAccounting ?? false,
            };
            console.log("Submitting updated transaction:", updatedTransaction);
            await onSubmitTransaction(updatedTransaction);
            handleClose(); // Close modal on success
        } catch (err: any) {
            console.error("Failed to submit transaction:", err);
            setError("Failed to submit transaction. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // --- Render Logic ---
    const numpadLayout = [
        ['1', '2', '3'],
        ['4', '5', '6'],
        ['7', '8', '9'],
        ['.', '0', 'backspace']
    ];
    const canSubmit = !isSubmitting && parseFloat(amountString) > 0;

    const animationDuration = 'duration-300';

    return (
        <>
            <div
                className={`fixed ${mode === 'split' ? 'rounded-t-xl' : ''} inset-0 flex flex-col items-center justify-center z-50 transition-opacity ${animationDuration} ease-in ${isVisible && !isAnimatingOut ? 'bg-black/50 opacity-100 backdrop-blur-sm' : 'bg-black/0 opacity-0'} px-4`}
                onClick={handleClose}
            >
                <div
                    className={`bg-card border border-border/80 text-foreground rounded-3xl shadow-2xl w-full max-w-sm max-h-[calc(100dvh-2rem)] overflow-hidden flex flex-col origin-bottom transition-[transform,opacity] ${animationDuration} ease-in-out ${isVisible && !isAnimatingOut ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-[200px] scale-50'}`}
                    onClick={(e) => e.stopPropagation()}
                >
                    
                    {/* Top Input Area */}
                    <div className="p-4 pt-6 flex-shrink-0">
                        {error && <p className="text-red-500 text-center mb-2 text-sm">{error}</p>}

                        {/* Show max amount hint in split mode */}
                        {mode === 'split' && maxAmount !== undefined && (
                            <div className="flex flex-row justify-center items-center mb-3">
                                <p className="text-xs text-muted-foreground text-center mr-2">Splitting from</p>
                                <CurrencyDisplay amount={maxAmount} showType={false} className="font-medium" />
                            </div>
                        )}

                        {/* Description & Date Input */}
                        <div className="flex justify-between items-center mb-3">
                            <input
                                type="text"
                                value={description}
                                onChange={(e) => {
                                    setDescription(e.target.value);
                                }}
                                placeholder="Paid to?"
                                className="flex-grow min-w-0 h-11 bg-background/40 border border-dashed border-border rounded px-3 py-1.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary mr-2"
                                disabled={isSubmitting}
                            />
                            {/* Date Button - Needs a way to select date */}
                            <button
                                className="h-11 bg-background/40 border border-dashed border-border rounded px-2 py-1.5 text-sm text-muted-foreground whitespace-nowrap"
                                disabled={isSubmitting}
                            >
                                <input
                                    type="date"
                                    value={transactionDateInputValue}
                                    className="h-full bg-transparent border-none focus:outline-none text-sm text-foreground"
                                    max={getTodayKolkataDateInputValue()}
                                    onChange={(e) => {
                                        setTransactionDateInputValue(e.target.value);
                                    }}
                                />
                            </button>
                        </div>

                        {/* Amount Display & Tag Button */}
                        <div className={`flex justify-between items-center mb-3`}>
                            <CurrencyDisplay 
                                amount={parseFloat(amountString) || 0} 
                                smallRupeeSymbol={true}
                                className="text-3xl font-semibold"
                                type={transactionType}
                            />
                            {/* Tag Button - Now opens TagSelector */}
                            <Badge
                                as="button"
                                variant="default"
                                size="md"
                                onClick={openTagSelector}
                                className="flex items-center gap-1 hover:bg-input"
                            >
                                <FiTag size={14} className="text-muted-foreground" />
                                <span>{tagName}</span>
                            </Badge>
                        </div>
                    </div>

                    {/* Numpad Area */}
                    <div className="flex-1 min-h-0 overflow-y-auto p-3 border-t border-border">
                        {/* Type Toggle Buttons (+/-) - Only show in create mode */}
                        {mode === 'create' && (
                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <button
                                    onClick={() => setTransactionTypeAndSync('CREDIT')}
                                    disabled={isSubmitting}
                                    className={`py-3 rounded-xl font-semibold flex items-center justify-center appearance-none [-webkit-tap-highlight-color:transparent] transition-none ${
                                        transactionType === 'CREDIT'
                                            ? 'bg-success/70 border border-success/40 text-success-foreground hover:bg-success/75 active:bg-success/75'
                                            : 'bg-input/70 border border-border/70 text-foreground/95 hover:bg-input active:bg-input'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    <FiPlus size={24} style={{ strokeWidth: 2.8 }} />
                                </button>
                                <button
                                    onClick={() => setTransactionTypeAndSync('DEBIT')}
                                    disabled={isSubmitting}
                                    className={`py-3 rounded-xl font-semibold flex items-center justify-center appearance-none [-webkit-tap-highlight-color:transparent] transition-none ${
                                        transactionType === 'DEBIT'
                                            ? 'bg-destructive/70 border border-destructive/40 text-destructive-foreground hover:bg-destructive/75 active:bg-destructive/75'
                                            : 'bg-input/70 border border-border/70 text-foreground/95 hover:bg-input active:bg-input'
                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    <FiMinus size={24} style={{ strokeWidth: 2.8 }} />
                                </button>
                            </div>
                        )}

                        {/* Numpad */}
                        <div className="grid grid-cols-3 gap-3">
                            {numpadLayout.flat().map((key) => (
                                <button
                                    key={key}
                                    onClick={() => key === 'backspace' ? handleBackspace() : handleNumpadClick(key)}
                                    className="py-3 rounded-xl text-xl font-semibold bg-input/70 border border-border/70 text-foreground flex items-center justify-center transition duration-100 ease-in-out transform active:scale-95 hover:bg-input"
                                    disabled={isSubmitting}
                                >
                                    {key === 'backspace' ? <FiDelete /> : key}
                                </button>
                            ))}
                        </div>

                        {/* Action Buttons */}
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <button
                                onClick={handleClose}
                                className="py-2.5 rounded-lg bg-input/70 hover:bg-input text-foreground font-semibold border border-border/80"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className={`py-2.5 rounded-lg font-semibold transition-colors ${
                                    canSubmit
                                        ? 'bg-primary text-primary-foreground hover:bg-primary/90 dark:bg-zinc-200 dark:text-zinc-900 dark:hover:bg-zinc-100'
                                        : 'bg-input text-muted-foreground'
                                }`}
                                disabled={!canSubmit}
                            >
                                {isSubmitting ? 'Submitting...' : 'Submit'} {/* Changed from Create to Submit */}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tag Selector with higher z-index (70) to appear above AmountInputModal (z-50) */}
            <DraggableBottomSheet 
                isOpen={isTagSelectorOpen} 
                onClose={closeTagSelector}
                zIndex={70}
                title="Tag Transaction"
            >
                <TagSelector
                    onSelectTag={handleSelectTag}
                    availableTags={availableTags}
                    tagMap={tagMap}
                    currentTagName={selectedTagName ?? undefined}
                    transaction={transaction}
                />
            </DraggableBottomSheet>
        </>
    );
};

export default AmountInputModal;
